/* Progressive storefront interactions. No price constants, payment handling or paid download URLs. */
export const MOTION_LIMIT = 4;
export function tiltPosition(x, y, width, height) {
  if (!(width > 0 && height > 0)) return { x: 0, y: 0 };
  const clamp = value => Math.max(-MOTION_LIMIT, Math.min(MOTION_LIMIT, value));
  return { x: clamp((.5 - y / height) * MOTION_LIMIT * 2), y: clamp((x / width - .5) * MOTION_LIMIT * 2) };
}
export function visibleKeys(items, group) {
  return items.filter(item => group === 'all' || item.group === group).map(item => item.key);
}
if (typeof document !== 'undefined') {
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const fine = matchMedia('(hover: hover) and (pointer: fine)');
  const lowData = matchMedia('(prefers-reduced-data: reduce)');
  const initialised = new WeakSet();
  function init(root = document) {
    root.querySelectorAll('[data-depth]').forEach(element => {
      if (initialised.has(element)) return;
      initialised.add(element);
      let frame = null;
      const reset = () => {
        if (frame !== null) cancelAnimationFrame(frame);
        frame = null;
        element.style.removeProperty('--depth-x');
        element.style.removeProperty('--depth-y');
      };
      element.addEventListener('pointermove', event => {
        if (motion.matches || lowData.matches || !fine.matches || frame !== null) return;
        frame = requestAnimationFrame(() => {
          frame = null;
          const rect = element.getBoundingClientRect();
          const value = tiltPosition(event.clientX - rect.left, event.clientY - rect.top, rect.width, rect.height);
          element.style.setProperty('--depth-x', value.x.toFixed(2) + 'deg');
          element.style.setProperty('--depth-y', value.y.toFixed(2) + 'deg');
        });
      });
      element.addEventListener('pointerleave', reset);
      element.addEventListener('pointercancel', reset);
      // Abort listeners when a theme-editor section is removed; no repeated global animation loops.
      const abort = new AbortController();
      [motion, fine, lowData].forEach(query => query.addEventListener('change', reset, { signal: abort.signal }));
      element.addEventListener('op2:destroy', () => { reset(); abort.abort(); }, { once: true });
    });
    if ('IntersectionObserver' in window && !motion.matches) {
      const observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      }, { threshold: .08 });
      root.querySelectorAll('.op2-card,.op2-offer,.op2-first-step').forEach(element => {
        if (!element.classList.contains('is-revealed')) observer.observe(element);
      });
    }
  }
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-depth]').forEach(e => e.dispatchEvent(new Event('op2:destroy'))));
  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const filter = target.closest('[data-filter]');
    if (filter) {
      const section = filter.closest('section');
      section.querySelectorAll('[data-filter]').forEach(button => {
        const active = button === filter;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      let count = 0;
      section.querySelectorAll('[data-group]').forEach(card => {
        const show = filter.dataset.filter === 'all' || filter.dataset.filter === card.dataset.group;
        card.hidden = !show;
        if (show) count++;
      });
      const status = section.querySelector('[role="status"]');
      if (status) status.textContent = count + ' current bundles shown. No selection was added to the cart.';
      return;
    }
    const thumb = target.closest('[data-gallery-thumb]');
    if (thumb && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      const gallery = thumb.closest('[data-gallery]');
      const main = gallery?.querySelector('[data-gallery-main]');
      if (!main) return;
      event.preventDefault();
      const source = thumb.querySelector('img');
      main.src = thumb.href;
      main.alt = source?.alt || 'Actual product file preview';
      main.closest('a').href = thumb.href;
      gallery.querySelector('.op2-gallery-main').classList.toggle('is-page', thumb.dataset.kind !== 'cover');
      gallery.querySelectorAll('[data-gallery-thumb]').forEach(item => {
        item.classList.toggle('is-active', item === thumb);
        if (item === thumb) item.setAttribute('aria-current', 'true');
        else item.removeAttribute('aria-current');
      });
      const caption = gallery.querySelector('[data-gallery-caption]');
      if (caption) caption.textContent = main.alt + (thumb.dataset.kind === 'proof' ? '. Exact blank headings are re-rendered; not an application screenshot.' : '. Digital file preview. Any example records are fictional.');
    }
  });
  document.addEventListener('keydown', event => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const thumb = document.activeElement?.closest('[data-gallery-thumb]');
    if (!thumb) return;
    const all = [...thumb.closest('[data-gallery]').querySelectorAll('[data-gallery-thumb]')];
    let index = all.indexOf(thumb);
    if (event.key === 'ArrowLeft') index = (index + all.length - 1) % all.length;
    if (event.key === 'ArrowRight') index = (index + 1) % all.length;
    if (event.key === 'Home') index = 0;
    if (event.key === 'End') index = all.length - 1;
    event.preventDefault(); all[index].focus(); all[index].click();
  });
  const sticky = document.querySelector('[data-sticky-purchase]');
  const panel = document.getElementById('purchase-panel');
  // A review link, not a second payment form. Hidden near the footer, dialogs, forms or privacy UI.
  if (sticky && panel) {
    let queued = false;
    const update = () => {
      queued = false;
      const footer = document.querySelector('.footer');
      const focus = document.activeElement;
      const editing = focus?.matches('input,textarea,select,[contenteditable="true"]');
      const privacy = document.querySelector('#shopify-pc__banner');
      const privacyVisible = privacy && privacy.getClientRects().length > 0;
      const availableHeight = window.visualViewport?.height || innerHeight;
      const nearFooter = footer && footer.getBoundingClientRect().top < innerHeight + 100;
      sticky.hidden = panel.getBoundingClientRect().bottom > 0 || nearFooter || Boolean(document.querySelector('dialog[open]')) || editing || privacyVisible || availableHeight < 480;
    };
    const queue = () => { if (!queued) { queued = true; requestAnimationFrame(update); } };
    addEventListener('scroll', queue, { passive: true });
    addEventListener('resize', queue, { passive: true });
    document.addEventListener('focusin', queue);
    document.addEventListener('focusout', queue);
    document.addEventListener('click', queue);
    document.addEventListener('close', queue, true);
    window.visualViewport?.addEventListener('resize', queue);
    update();
  }
}
